<template>
  <div v-if="subscriptionsLoaded">
    <!-- Subscription -->
    <div>
      <!-- Active Subscriptions -->
      <div v-if="subscriptions.length">
        <h3 class="mb-3 text-lg font-bold leading-6 text-gray-900 capitalize dark:text-gray-50 sm:text-xl">Subscription</h3>
        <div class="p-4 text-sm bg-white rounded-lg shadow-lg dark:bg-gray-900 sm:space-y-5 sm:text-base md:p-6">
          <div v-if="subscriptions.length" class="space-y-3">
            <template v-for="subscription in subscriptions" :key="subscription">
              <!-- Status Message -->
              <div v-if="minutesBetween(subscription.created.seconds, new Date() / 1000) < 2 && subscription.status == 'active' && !subscription.cancel_at" class="mb-5">
                <div class="p-4 rounded-md bg-green-50 dark:bg-green-900">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <CheckCircleIcon class="w-5 h-5 text-green-400 dark:text-green-400" aria-hidden="true" />
                    </div>
                    <div class="ml-3">
                      <h3 class="text-sm font-medium text-green-800 dark:text-green-300">Order completed</h3>
                      <div class="mt-2 text-sm text-green-700 dark:text-green-300">
                        <p>Thank you for subscribing to Wisery! For any questions or feedback, reach out to us anytime at hello@wisery.io.</p>
                      </div>
                      <div class="mt-4">
                        <div class="-mx-2 -my-1.5 flex">
                          <router-link
                            to="/"
                            class="rounded-md bg-green-50 px-2 py-1.5 text-sm font-medium text-green-800 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-green-50 dark:bg-transparent dark:text-green-200"
                          >
                            Review your content
                          </router-link>
                          <router-link
                            to="/progress"
                            class="ml-3 rounded-md bg-green-50 px-2 py-1.5 text-sm font-medium text-green-800 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-green-50 dark:bg-transparent dark:text-green-200"
                          >
                            Check your progress
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex items-center justify-between sm:justify-start">
                <div class="flex items-center w-1/2 space-x-1 font-medium text-gray-700 dark:text-gray-200">
                  <span>Status</span>
                </div>
                <div class="text-right text-gray-600 dark:text-gray-400">
                  <span class="capitalize">{{ subscription.status }}</span>
                  <span v-if="subscription.cancel_at" class="ml-1">until {{ dateString(subscription.current_period_end.seconds, true) }}</span>
                </div>
              </div>

              <div class="flex items-center justify-between sm:justify-start">
                <div class="flex items-center w-1/2 space-x-1 font-medium text-gray-700 dark:text-gray-200">
                  <span>Price</span>
                </div>
                <div class="flex text-right item-center">
                  <div class="text-gray-600 dark:text-gray-400">
                    {{ subscription.items[0].plan.amount / 100 }} <span class="uppercase">{{ subscription.items[0].plan.currency }}</span>
                    <span>/{{ subscription.items[0].plan.interval }}</span>
                  </div>
                </div>
              </div>

              <div class="flex items-center justify-between sm:justify-start">
                <div class="flex items-center w-1/2 space-x-1 font-medium text-gray-700 dark:text-gray-200">
                  <span>Started on</span>
                </div>
                <div class="flex item-center">
                  <span class="text-gray-600 dark:text-gray-400"> {{ dateString(subscription.current_period_start.seconds, true) }}</span>
                </div>
              </div>

              <div class="flex items-center justify-between sm:justify-start">
                <div class="flex items-center w-1/2 space-x-1 font-medium text-gray-700 dark:text-gray-200">
                  <span>Renews on</span>
                </div>
                <div class="flex item-center">
                  <span class="text-gray-600 dark:text-gray-400">{{ dateString(subscription.current_period_end.seconds, true) }}</span>
                </div>
              </div>

              <div class="flex items-center justify-between sm:justify-start">
                <div class="flex items-center w-1/2 space-x-1 font-medium text-gray-700 dark:text-gray-200">
                  <span>Update subscription, view invoices</span>
                </div>
                <div class="flex space-x-2 item-center">
                  <span v-if="!stripeCustomerPortalLink" class="text-gray-600 dark:text-gray-400">Loading...</span>
                  <a v-else :href="stripeCustomerPortalLink" class="text-blue-600 dark:text-blue-400">Manage Subscription</a>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- No active subscription on /profile -->
      <div v-if="!subscriptions.length && currentRoutePath == '/profile'">
        <h3 class="mb-3 text-lg font-bold leading-6 text-gray-900 capitalize dark:text-gray-50 sm:text-xl">Subscription</h3>
        <div class="p-4 text-xs bg-white rounded-lg shadow-lg dark:bg-gray-900 sm:space-y-5 sm:text-sm md:p-6">
          <span class="text-right text-gray-600 dark:text-gray-400">No active subscription. <router-link to="/subscription" class="text-blue-600 dark:text-blue-400">Subscribe now.</router-link></span>
        </div>
      </div>
    </div>

    <!-- No active subscription on /subscription -->
    <div v-if="prices.length && !subscriptions.length && currentRoutePath === '/subscription'">
      <div class="text-center">
        <h2 class="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl lg:text-5xl">Simple pricing</h2>
        <p class="mt-4 text-xl text-gray-600 dark:text-gray-400">Grow your knowledge exponentially for the price of a cup of coffee.</p>
      </div>

      <div class="pb-16 mt-8 sm:mt-12 sm:pb-20 lg:pb-28">
        <div class="relative">
          <div class="mx-auto overflow-hidden rounded-lg shadow-lg lg:flex lg:max-w-none">
            <div class="flex-1 px-6 py-8 bg-white dark:bg-gray-900 lg:p-12">
              <h3 class="text-2xl font-extrabold text-gray-900 dark:text-white sm:text-3xl">Invest little, gain a lot</h3>
              <p class="mt-6 text-base text-gray-500 dark:text-gray-400">
                If you want to progress in your career, studies or generally increase your knowledge, this is probably the highest ROI you can get for ${{ prices[0].amount / 100 }}/{{
                  prices[0].interval
                }}.
              </p>
              <div class="mt-8">
                <div class="flex items-center">
                  <h4 class="flex-shrink-0 pr-4 text-sm font-semibold tracking-wider text-gray-600 uppercase bg-white dark:bg-gray-900 dark:text-gray-400">What's included</h4>
                  <div class="flex-1 border-t-2 border-gray-200 dark:border-gray-400" />
                </div>
                <ul role="list" class="block mt-8 space-y-5">
                  <li v-for="feature in includedFeatures" :key="feature" class="flex items-start">
                    <div class="flex-shrink-0">
                      <CheckCircleIcon class="w-5 h-5 text-green-400 dark:text-green-600" aria-hidden="true" />
                    </div>
                    <p class="ml-3 text-sm text-gray-700 dark:text-gray-300">
                      {{ feature }}
                    </p>
                  </li>
                </ul>
              </div>
            </div>

            <div
              class="flex flex-col justify-center flex-shrink-0 w-auto px-6 py-8 space-y-8 text-center bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 dark:from-slate-800 dark:via-gray-800 dark:to-slate-800 lg:w-1/2 lg:p-12"
            >
              <div class="relative flex w-full justify-center self-center rounded-lg bg-gray-100 p-0.5 dark:bg-gray-700 sm:w-auto">
                <template v-for="price in prices" :key="price">
                  <button
                    @click="monthlyBilling = !monthlyBilling"
                    type="button"
                    class="relative w-1/2 py-2 text-sm font-medium text-gray-900 rounded-md shadow-sm whitespace-nowrap focus:outline-none sm:w-auto sm:px-8"
                    :class="{
                      'dark:border-bg-gray-800 border-gray-200 bg-white dark:bg-black dark:text-white':
                        (monthlyBilling && price.interval === 'month') || (!monthlyBilling && price.interval === 'year'),
                    }"
                  >
                    <span class="capitalize"> {{ price.interval }}ly</span>
                  </button>
                </template>
              </div>
              <div class="flex items-center justify-center mt-4 text-5xl font-extrabold text-gray-900 dark:text-white">
                <span>$</span>
                <template v-for="price in prices" :key="price">
                  <span v-if="monthlyBilling && price.interval == 'month'"> {{ price.amount / 100 }} </span>
                  <span v-if="!monthlyBilling && price.interval == 'year'"> {{ price.amount / 100 }} </span>
                </template>
                <span class="ml-3 text-xl font-medium text-gray-500 uppercase"> {{ prices[0].currency }} </span>
              </div>

              <div class="mx-auto text-sm text-gray-500 dark:text-gray-400">
                If you can't afford this price, <a href="mailto:hello@wisery.io" class="underline"> please reach out to us</a>. We approve all requests, no questions asked.
              </div>

              <div class="mt-6">
                <div class="rounded-md shadow">
                  <button
                    :disabled="checkoutLoading"
                    @click="initiateCheckout()"
                    class="flex items-center justify-center w-full px-5 py-3 text-base font-medium text-white bg-gray-800 border border-transparent rounded-md hover:bg-gray-900 disabled:cursor-wait disabled:opacity-20 dark:bg-gray-300 dark:text-gray-900 dark:hover:bg-gray-100"
                  >
                    <LoadingSpinner class="w-5 h-5 mr-2" v-if="checkoutLoading" />
                    {{ checkoutLoading ? "Creating Payment Link" : "Get Access" }}
                  </button>
                </div>
              </div>
              <div class="mt-4 text-sm">
                <span class="font-medium text-gray-900 dark:text-white">Join {{ numMembers.toLocaleString() }} other Wisery members.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CheckCircleIcon } from "@heroicons/vue/solid";
import { computed, ref, watch, watchEffect } from "vue";
import { collection, getDocs, query, where, limit, doc, onSnapshot, addDoc } from "firebase/firestore";
import utilFunctions from "../composables/util";
import { db } from "../firebase";
import { useToast } from "vue-toastification";
import firebaseUser from "../composables/userAuth";
import LoadingSpinner from "./LoadingSpinner.vue";
import { useRoute } from "vue-router";

export default {
  components: {
    CheckCircleIcon,
    LoadingSpinner,
  },
  setup() {
    const { dateString, minutesBetween, stripeCustomerPortal, stripePrices } = utilFunctions();
    const monthlyBilling = ref(true);
    const randomNumber = new Date().getTime() / 100000000;
    const numMembers = ref(randomNumber.toFixed(0));
    const toast = useToast();
    const { user } = firebaseUser();
    const includedFeatures = ["Save unlimited content", "Unlimited personalized reviews", "Detailed progress tracking", "24/7 support"];
    const checkoutLoading = ref(false);
    const prices = ref([]);
    const subscriptions = ref([]);
    const subscriptionsLoaded = ref(false);
    const currentRoutePath = computed(() => {
      return useRoute().path;
    });

    const randomNumberInRange = (min, max) => {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    };

    async function initiateCheckout() {
      checkoutLoading.value = true;
      let price = null;
      if (monthlyBilling.value) {
        price = prices.value.find((price) => price.interval === "month");
      } else {
        price = prices.value.find((price) => price.interval === "year");
      }

      try {
        const docRef = await addDoc(collection(doc(db, "users", user.value.uid), "checkout_sessions"), {
          price: price.id,
          success_url: window.location.href,
          cancel_url: window.location.href,
          allow_promotion_codes: true,
        });

        onSnapshot(docRef, (doc) => {
          const { error, url } = doc.data();

          if (error) toast.error(error.message);

          if (url) {
            checkoutLoading.value = false;
            window.location.assign(url);
          }
        });
      } catch (error) {
        toast.error(error.message);
        console.log(error.message);
      }
    }

    // increment numMember
    setInterval(() => {
      numMembers.value++;
    }, randomNumberInRange(3000, 8000));

    const stripeCustomerPortalLink = ref(null);

    watchEffect(async () => {
      if (user.value) {
        // get subscription(s)
        const subscriptionQuery = query(collection(db, "users", user.value.uid, "subscriptions"), where("status", "in", ["trialing", "active"]));
        const subscriptionSnap = await getDocs(subscriptionQuery);
        subscriptionsLoaded.value = true;

        if (!subscriptionSnap.empty) {
          subscriptions.value.push(subscriptionSnap.docs[0].data());
        }

        // get product prices
        const getStripePrices = await stripePrices();
        if (getStripePrices.length) {
          getStripePrices.forEach(async (price) => {
            prices.value.push({
              id: price.id,
              interval: price.interval,
              amount: price.unit_amount,
              currency: price.currency,
              description: price.description,
            });
          });
        }

        const productQuery = query(collection(db, "products"), where("active", "==", true), limit(1));
        const productSnap = await getDocs(productQuery);
        productSnap.forEach(async (subscriptionDoc) => {
          const pricesSnap = await getDocs(collection(subscriptionDoc.ref, "prices"));
          pricesSnap.forEach(async (priceDoc) => {
            const price = priceDoc.data();
            prices.value.push({
              id: priceDoc.id,
              interval: price.interval,
              amount: price.unit_amount,
              currency: price.currency,
              description: price.description,
            });
          });
        });
      }
    });

    watch(user, async (userValue) => {
      if (userValue) {
        stripeCustomerPortalLink.value = await stripeCustomerPortal();
      }
    });

    return {
      dateString,
      minutesBetween,
      includedFeatures,
      numMembers,
      monthlyBilling,
      prices,
      initiateCheckout,
      checkoutLoading,
      subscriptions,
      stripeCustomerPortalLink,
      currentRoutePath,
      subscriptionsLoaded,
    };
  },
};
</script>
